import { ReactNode, useEffect, useMemo, useState } from 'react';
import {
  createFileRoute,
  Navigate,
  Outlet,
  useRouterState,
} from '@tanstack/react-router';
import NotFoundContent from 'src/components/layout/NotFoundContent';
import PageLoader from 'src/components/ui/PageLoader';
import useUser from 'src/hooks/useUser';
import { useMediaQuery } from 'react-responsive';
import Profile, { ProfileMenuTrigger } from 'src/components/ui/Profile';
import Sidebar, {
  MobileSidebarTrigger,
  SidebarToggleButton,
  useSidebarToggle,
} from 'src/components/Sidebar';
import SkipToMainContent, {
  MAIN_CONTENT_ID,
} from 'src/components/layout/SkipToMainContent';
import SidebarMenu, { SidebarItem } from 'src/components/creator/SidebarMenu';
import CoursePacksIcon from 'src/assets/icons/course-pack-icon.svg';
// import MaterialsIcon from 'src/assets/icons/materials-icon.svg'; // TODO: Uncomment when materials feature is ready
import CloseIcon from 'src/assets/icons/close.svg';
import ContactIcon from 'src/assets/icons/support-icon.svg';
import PrinterIcon from 'src/assets/icons/printer.svg';
import FAQIcon from 'src/assets/icons/help-question.svg';

const CreatorPage = () => {
  const { user, loading, error } = useUser();

  if (loading) return <PageLoader />;

  if (error) return <Navigate to="/login" />;

  if (user.role !== 'CREATOR') return <Navigate to="/dashboard" />;

  return (
    <Container>
      <Outlet />
    </Container>
  );
};

const CreatorSidebar = ({
  forceExpanded = true,
  isMobile = false,
  onClose,
}: {
  forceExpanded?: boolean;
  isMobile?: boolean;
  onClose?: () => void;
}) => {
  const { user } = useUser();
  const { expanded, onToggle } = useSidebarToggle();

  const isExpanded = useMemo(() => {
    if (isMobile) return true;
    if (!forceExpanded) return false;

    return expanded;
  }, [expanded, forceExpanded, isMobile]);

  return (
    <Sidebar
      type="creator"
      isExpanded={isExpanded}
      onExpandToggle={!forceExpanded || isMobile ? undefined : onToggle}
      footer={
        <div className="flex justify-between gap-2">
          <Profile
            className="flex-1"
            size={isExpanded ? 'medium' : 'small'}
            actionSlot={
              <ProfileMenuTrigger className={!isExpanded ? 'w-fit' : ''} />
            }
          />
          {isMobile && (
            <SidebarToggleButton
              aria-label="Close Sidebar"
              Icon={CloseIcon}
              onPress={onClose}
              className="xs:-mr-16"
            />
          )}
        </div>
      }
    >
      <SidebarMenu title={isExpanded ? 'General' : undefined} className="mt-4">
        <SidebarItem
          label="Dashboard"
          Icon={CoursePacksIcon}
          isExpanded={isExpanded}
          to="/requests"
        />
        {/* TODO: Uncomment when materials feature is ready for production */}
        {/* <SidebarItem
          label="Materials"
          Icon={MaterialsIcon}
          isExpanded={isExpanded}
          to="/materials"
        /> */}
        <SidebarItem
          label="Out-of-Print Library"
          Icon={PrinterIcon}
          isExpanded={isExpanded}
          to="/out-of-print"
        />
        {Boolean(user?.hasLadLibrarySearch) && (
          <SidebarItem
            label="LAD Library"
            Icon={PrinterIcon}
            isExpanded={isExpanded}
            to="/lad-library"
          />
        )}
        <SidebarItem
          label="FAQ"
          Icon={FAQIcon}
          isExpanded={isExpanded}
          to="/creator-faq"
        />
        <SidebarItem
          label="Contact Us"
          Icon={ContactIcon}
          isExpanded={isExpanded}
          to="/creator-support"
        />
      </SidebarMenu>
    </Sidebar>
  );
};

const MobileSidebar = () => {
  const { isLoading } = useRouterState();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (open && isLoading) setOpen(false);
  }, [isLoading, open]);

  return (
    <MobileSidebarTrigger isOpen={open} onOpenChange={setOpen}>
      <CreatorSidebar isMobile onClose={() => setOpen(false)} />
    </MobileSidebarTrigger>
  );
};

const Container = ({ children }: { children: ReactNode }) => {
  const isMobile = useMediaQuery({
    query: '(max-width: 640px)',
  });

  const isExpanded = useMediaQuery({
    query: '(min-width: 769px)',
  });

  return (
    <>
      <SkipToMainContent />
      {isMobile && <MobileSidebar />}
      <div className="px-2 sm:px-4 lg:px-8 h-full">
        <div className="flex items-stretch h-full">
          {!isMobile && <CreatorSidebar forceExpanded={isExpanded} />}
          <main
            id={MAIN_CONTENT_ID}
            className="flex-1 min-w-0 flex flex-col pl-4 pt-2 pb-1 sm:pb-2 lg:pb-8 h-full overflow-hidden"
          >
            {children}
          </main>
        </div>
      </div>
    </>
  );
};

export const Route = createFileRoute('/_authenticated/_creator')({
  component: CreatorPage,
  notFoundComponent: () => <NotFoundContent className="mt-2" />,
});
